import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';

export function TechCard({ title, icon: Icon, children, headerRight, style }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '2px solid #1a3a5c',
        boxShadow: '5px 5px 0 #2563a8',
        overflow: 'hidden',
        ...style,
      }}
    >
      {title && (
        <div style={{
          background: '#1a3a5c',
          color: '#fff',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {Icon && <Icon size={16} />}
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase' }}>
              {title}
            </span>
          </div>
          {headerRight}
        </div>
      )}
      <div style={{ padding: 14 }}>
        {children}
      </div>
    </div>
  );
}

// Traduction des couleurs « naturelles » vers les variants du composant.
// Motif (2026-08-26) : 6 appels en production passent `color="green"`,
// `color="red"`, `color="gray"` ou une valeur dynamique (`TonersPage` envoie
// blue/purple/green/orange). Aucune n'etant un variant, TOUS ces badges
// rendaient la pastille grise — le badge « err » du tableau de bord
// fournisseurs n'a ainsi jamais rien signale visuellement.
// Une couleur sans equivalent (purple, gray) retombe volontairement sur le
// badge neutre plutot que d'inventer une teinte.
const BADGE_COLOR_TO_VARIANT = {
  green: 'success',
  red: 'error',
  orange: 'warning',
  amber: 'warning',
  yellow: 'warning',
  blue: 'info',
};

export function Badge({ label, children, variant, color, style, title }) {
  const colors = {
    default: { bg: '#e8f0fe', color: '#1a3a5c', border: '#c0cfe0' },
    success: { bg: '#eafaf1', color: '#27ae60', border: '#27ae60' },
    error: { bg: '#fde8e8', color: '#c0392b', border: '#c0392b' },
    warning: { bg: '#fef9e7', color: '#e67e22', border: '#e67e22' },
    info: { bg: '#e8f0fe', color: '#2563a8', border: '#2563a8' },
  };
  // `variant` reste la voie principale ; `color` n'est qu'un repli pour les
  // appels historiques, jamais un moyen de le contredire.
  const resolved = variant
    || BADGE_COLOR_TO_VARIANT[String(color || '').toLowerCase()]
    || 'default';
  const c = colors[resolved] || colors.default;
  // Tolère deux conventions d'appel : <Badge label="x" /> et <Badge>x</Badge>.
  // Sans ce fallback, les appels en children rendaient une pastille vide.
  const content = label != null ? label : children;
  return (
    <span title={title} style={{
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: 11,
      fontWeight: 500,
      padding: '2px 8px',
      border: `1px solid ${c.border}`,
      borderRadius: 2,
      background: c.bg,
      color: c.color,
      whiteSpace: 'nowrap',
      ...(style || {}),
    }}>
      {content}
    </span>
  );
}

export function DataTable({ columns, data, emptyText = 'Aucune donnee' }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th key={i} style={{
                background: '#e8f0fe',
                color: '#1a3a5c',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                padding: '8px 10px',
                borderBottom: '2px solid #1a3a5c',
                textAlign: 'left',
                whiteSpace: 'nowrap',
              }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ padding: 20, textAlign: 'center', color: '#6b7d8f', fontStyle: 'italic' }}>
                {emptyText}
              </td>
            </tr>
          ) : (
            data.map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #c0cfe0', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                {columns.map((col, j) => (
                  <td key={j} style={{
                    padding: '8px 10px',
                    fontFamily: col.mono ? "'IBM Plex Mono', monospace" : 'inherit',
                    fontSize: col.mono ? 12 : 13,
                  }}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function StatBox({ label, value, sub, icon: Icon, variant = 'default' }) {
  const bgMap = { default: '#f4f7fb', success: '#eafaf1', error: '#fde8e8', warning: '#fef9e7' };
  const colorMap = { default: '#1a3a5c', success: '#27ae60', error: '#c0392b', warning: '#e67e22' };
  return (
    <div style={{
      background: bgMap[variant] || bgMap.default,
      border: '1px solid #c0cfe0',
      borderRadius: 4,
      padding: '14px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    }}>
      {Icon && <Icon size={24} style={{ color: colorMap[variant] || colorMap.default, flexShrink: 0 }} />}
      <div>
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 700, color: colorMap[variant] || colorMap.default }}>
          {value}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7d8f', marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: '#6b7d8f', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

// Tolère deux conventions d'appel pour la barre d'actions : <PageHeader actions={...} />
// et <PageHeader>...</PageHeader>. 13 pages du portail (CRM, MCF, Microsoft 365, Social)
// passent leurs boutons en enfants ; sans ce support React les jetait silencieusement
// et les boutons étaient invisibles en production. Ne pas retirer `children` lors d'un
// futur nettoyage sans avoir migré ces pages vers la prop `actions`.
export function PageHeader({ title, subtitle, actions, breadcrumb, icon: Icon, afterHeader, children }) {
  // Le fil d'Ariane du portail (NavigationTrail) s'ancre sous .page-header ;
  // avec des pages chargées à la demande, il doit savoir QUAND le bandeau est là.
  useLayoutEffect(() => {
    try { window.dispatchEvent(new CustomEvent('page-header-mounted')); } catch (e) { /* SSR / tests */ }
  }, []);
  return (
    <>
    <div className="page-header" style={{
      background: '#1a3a5c',
      color: '#fff',
      padding: '14px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 12,
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
    }}>
      <div>
        {breadcrumb && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
            {breadcrumb.map((crumb, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ margin: '0 2px' }}>/</span>}
                {crumb.path ? (
                  <a href={crumb.path} style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}
                    onMouseEnter={e => e.target.style.color = '#fff'}
                    onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.6)'}
                  >{crumb.label}</a>
                ) : (
                  <span style={{ color: 'rgba(255,255,255,0.8)' }}>{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {Icon && <Icon size={18} style={{ color: 'rgba(255,255,255,0.7)' }} />}
          <h1 style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 17, fontWeight: 700, letterSpacing: 1 }}>
            {title}
          </h1>
        </div>
        {subtitle && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>{subtitle}</p>}
      </div>
      {(actions || children) && (
        <div style={{ display: 'flex', gap: 8, flexShrink: 1, minWidth: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {actions}
          {children}
        </div>
      )}
    </div>
    {afterHeader}
    </>
  );
}

export function MockBadge() {
  return (
    <span style={{
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: 9,
      fontWeight: 700,
      padding: '2px 6px',
      background: '#e67e22',
      color: '#fff',
      borderRadius: 2,
      letterSpacing: 1,
    }}>
      MOCK
    </span>
  );
}

/**
 * StatBadge — Clickable filter badge with count.
 * Props:
 *   label: string
 *   value: number|string
 *   color: CSS color string (e.g. 'var(--success)')
 *   active: boolean — whether this filter is currently selected
 *   onClick: () => void
 */
export function StatBadge({ label, value, color, active = false, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
        background: active ? `${color}18` : 'var(--surface)',
        border: active ? `2px solid ${color}` : '1px solid var(--border-light)',
        borderRadius: 8, fontSize: 12,
        cursor: 'pointer',
        transition: 'all var(--duration-fast, 0.15s) var(--ease-out, ease-out)',
        fontFamily: "'IBM Plex Sans', sans-serif",
        transform: active ? 'scale(1.02)' : 'none',
      }}
    >
      <span style={{ color: active ? color : 'var(--text-muted, #6b7d8f)' }}>{label}</span>
      <span style={{ fontWeight: 700, color, fontFamily: "'IBM Plex Mono', monospace" }}>{value}</span>
    </button>
  );
}

/**
 * FilterBar — Container for StatBadge groups with optional separators.
 * Props:
 *   children: StatBadge elements (use FilterBar.Separator between groups)
 */
export function FilterBar({ children }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      {children}
    </div>
  );
}
FilterBar.Separator = function Separator() {
  return <div style={{ width: 1, height: 20, background: 'var(--border-light, #e8f0fe)', margin: '0 4px' }} />;
};

/**
 * EmptyState — Standardized empty/placeholder display.
 * Props:
 *   icon: Lucide icon component
 *   title: string
 *   description: string (optional)
 *   action: ReactNode (optional — e.g. a button)
 */
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      {Icon && <Icon size={48} style={{ color: '#c0cfe0', marginBottom: 16 }} />}
      <div style={{ fontSize: 16, fontWeight: 600, color: '#1a3a5c', marginBottom: 8 }}>
        {title}
      </div>
      {description && (
        <div style={{ fontSize: 13, color: '#6b7d8f', maxWidth: 400, margin: '0 auto' }}>
          {description}
        </div>
      )}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

// ── AudioPlayer ─────────────────────────────────────────────────────────────

/**
 * AudioPlayer — Reusable inline audio player with play/pause, progress, duration.
 * Works with any audio URL (OneDrive, direct file, etc.)
 *
 * Props:
 *   src: string — Audio URL (or null/undefined while loading)
 *   onLoadSrc: () => Promise<string> — Async function to fetch audio URL on first play
 *   label: string — Optional label displayed next to controls
 *   compact: boolean — Smaller variant for tight spaces (default: false)
 */
export function AudioPlayer({ src: initialSrc, onLoadSrc, label, compact = false }) {
  const audioRef = useRef(null);
  const [src, setSrc] = useState(initialSrc || null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const speedSteps = [1, 1.25, 1.5, 2];

  useEffect(() => { if (initialSrc) setSrc(initialSrc); }, [initialSrc]);

  // Audio is loaded on-demand when user clicks play (handlePlayPause below).
  // No pre-fetch: avoids N parallel audio downloads when a ticket has many call items.

  const cycleSpeed = () => {
    const idx = speedSteps.indexOf(playbackRate);
    const next = speedSteps[(idx + 1) % speedSteps.length];
    setPlaybackRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const formatTime = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!src && onLoadSrc) {
      setLoading(true);
      setError(null);
      try {
        const url = await onLoadSrc();
        if (url) {
          setSrc(url);
          // Wait for src to be set and audio to load
          setTimeout(() => {
            audioRef.current?.play().catch(() => {});
          }, 100);
        } else {
          setError('Audio indisponible');
        }
      } catch (e) {
        setError(e.message || 'Erreur chargement');
      }
      setLoading(false);
      return;
    }

    if (playing) {
      audio.pause();
    } else {
      try { await audio.play(); } catch (e) { setError('Lecture impossible'); }
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
  };

  const h = compact ? 36 : 44;
  const iconSize = compact ? 16 : 20;
  const fontSize = compact ? 11 : 12;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: compact ? 8 : 10,
      background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8,
      padding: compact ? '4px 10px' : '6px 12px', height: h,
      fontFamily: "'IBM Plex Sans', sans-serif",
    }}>
      {/* Hidden audio element — always mounted, src set when available */}
      <audio
        ref={audioRef}
        src={src || undefined}
        preload="metadata"
        onPlay={() => { setPlaying(true); if (audioRef.current) audioRef.current.playbackRate = playbackRate; }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onLoadedMetadata={() => { if (audioRef.current?.duration && isFinite(audioRef.current.duration)) setDuration(audioRef.current.duration); }}
        onError={() => { if (src) setError('Format non supporté'); }}
      />

      {/* Play/Pause button */}
      <button
        onClick={handlePlayPause}
        disabled={loading}
        style={{
          width: compact ? 28 : 32, height: compact ? 28 : 32,
          borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: error ? '#ef4444' : playing ? '#1a3a5c' : '#2563eb',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, transition: 'all 150ms',
          opacity: loading ? 0.6 : 1,
        }}
        title={playing ? 'Pause' : 'Lecture'}
      >
        {loading ? (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
            <path d="M21 12a9 9 0 11-6.219-8.56" />
          </svg>
        ) : error ? (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        ) : playing ? (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>
          </svg>
        ) : (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
            <polygon points="6,3 20,12 6,21"/>
          </svg>
        )}
      </button>

      {/* Progress + time */}
      {!error ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {/* Progress bar */}
          <div
            onClick={handleSeek}
            style={{
              flex: 1, height: 6, background: '#cbd5e1', borderRadius: 3,
              cursor: duration ? 'pointer' : 'default', position: 'relative', minWidth: 60,
            }}
          >
            <div style={{
              height: '100%', background: '#2563eb', borderRadius: 3,
              width: duration ? `${(currentTime / duration) * 100}%` : '0%',
              transition: 'width 0.1s linear',
            }} />
          </div>
          {/* Time */}
          <span style={{
            fontSize, color: '#64748b', fontFamily: "'IBM Plex Mono', monospace",
            flexShrink: 0, minWidth: compact ? 62 : 72,
          }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
      ) : (
        <span style={{ fontSize, color: '#ef4444', fontStyle: 'italic' }}>{error}</span>
      )}

      {/* Playback speed */}
      {!error && (
        <button
          onClick={cycleSpeed}
          style={{
            border: 'none', background: playbackRate !== 1 ? '#2563eb' : '#e2e8f0',
            color: playbackRate !== 1 ? '#fff' : '#475569',
            borderRadius: 4, padding: compact ? '1px 5px' : '2px 6px',
            fontSize: compact ? 10 : 11, fontWeight: 600, cursor: 'pointer',
            fontFamily: "'IBM Plex Mono', monospace", flexShrink: 0,
            transition: 'all 150ms', lineHeight: 1.4,
          }}
          title="Vitesse de lecture"
        >
          {playbackRate}x
        </button>
      )}

      {/* Optional label */}
      {label && !error && (
        <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      )}

      {/* Spin animation */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Toast notification system ────────────────────────────────────────────────

const ToastContext = createContext(null);

let _nextToastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const addToast = useCallback((message, variant = 'info', duration = 4000) => {
    const id = ++_nextToastId;
    // Garde-fou anti page-blanche : un message non-string (ex: `detail` d'un 422 FastAPI =
    // liste d'objets {type,loc,msg,...}) rendu en enfant React leve l'erreur #31 et demonte
    // TOUTE l'app. On coerce ici, au point d'entree unique de tous les toasts.
    let safeMessage = message;
    // Une Error (`formatApiError`, `catch (e)`) s'affiche par son TEXTE : JSON.stringify n'en garde que les champs
    // AJOUTÉS, le message n'étant pas énumérable — le toast montrait {"status":409,"isUpstreamUnavailable":false,
    // "rawDetail":"…"} (09/10/2026, « Déployer les outils »). `rawDetail` = le détail du serveur, sans « HTTP 409 : ».
    if (safeMessage instanceof Error) {
      safeMessage = (typeof safeMessage.rawDetail === 'string' && safeMessage.rawDetail) || safeMessage.message || '';
    }
    if (typeof safeMessage !== 'string') {
      try { safeMessage = safeMessage == null ? '' : JSON.stringify(safeMessage); }
      catch { safeMessage = String(safeMessage); }
    }
    setToasts(prev => [...prev, { id, message: safeMessage, variant }]);
    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => removeToast(id), duration);
    }
    return id;
  }, [removeToast]);

  const toast = useCallback({
    success: (msg, dur) => addToast(msg, 'success', dur),
    error: (msg, dur) => addToast(msg, 'error', dur ?? 6000),
    warning: (msg, dur) => addToast(msg, 'warning', dur ?? 5000),
    info: (msg, dur) => addToast(msg, 'info', dur),
  }, [addToast]);

  // Expose via object so destructuring works: const toast = useToast();
  const api = useRef(null);
  if (!api.current) {
    api.current = {
      success: (msg, dur) => addToast(msg, 'success', dur),
      error: (msg, dur) => addToast(msg, 'error', dur ?? 6000),
      warning: (msg, dur) => addToast(msg, 'warning', dur ?? 5000),
      info: (msg, dur) => addToast(msg, 'info', dur),
    };
  }
  // Keep refs fresh
  api.current._add = addToast;

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      {/* Toast container */}
      {toasts.length > 0 && (
        // Au-dessus de l'écran de blocage de la saisie quotidienne (100000) : sous lui, un refus ne se
        // voyait pas (Nassim, 20/10/2026 : modifications refusées sans un mot).
        <div style={{
          position: 'fixed', top: 70, right: 16, zIndex: 100020,
          display: 'flex', flexDirection: 'column', gap: 8,
          pointerEvents: 'none', maxWidth: 420,
        }}>
          {toasts.map(t => (
            <ToastItem key={t.id} toast={t} onClose={() => removeToast(t.id)} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

const TOAST_COLORS = {
  success: { bg: '#eafaf1', border: '#27ae60', color: '#1a6b3c', icon: '✓' },
  error: { bg: '#fde8e8', border: '#c0392b', color: '#922020', icon: '✕' },
  warning: { bg: '#fef9e7', border: '#e67e22', color: '#8a4b10', icon: '!' },
  info: { bg: '#e8f0fe', border: '#2563a8', color: '#1a3a5c', icon: 'i' },
};

function ToastItem({ toast, onClose }) {
  const c = TOAST_COLORS[toast.variant] || TOAST_COLORS.info;
  const [visible, setVisible] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);

  return (
    <div
      style={{
        background: c.bg, border: `1px solid ${c.border}`, borderLeft: `4px solid ${c.border}`,
        borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 10,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)', pointerEvents: 'auto',
        fontFamily: "'IBM Plex Sans', sans-serif",
        transform: visible ? 'translateX(0)' : 'translateX(100%)',
        opacity: visible ? 1 : 0,
        transition: 'transform 0.25s ease-out, opacity 0.25s ease-out',
      }}
    >
      <span style={{
        width: 20, height: 20, borderRadius: '50%', background: c.border, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, flexShrink: 0,
      }}>
        {c.icon}
      </span>
      <div style={{ flex: 1, fontSize: 13, color: c.color, lineHeight: 1.4 }}>
        {toast.message}
      </div>
      <button
        onClick={onClose}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 2,
          color: c.color, opacity: 0.5, fontSize: 16, lineHeight: 1, flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return {
      success: (msg) => {},
      error: (msg) => { if (typeof window !== 'undefined') window.alert(msg); },
      warning: (msg) => {},
      info: (msg) => {},
    };
  }
  return ctx;
}

// ── Confirm dialog system ────────────────────────────────────────────────────

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { title, message, resolve, variant, confirmLabel, cancelLabel }

  // Accepte 2 signatures :
  //   confirm('message', { variant, confirmLabel, ... })
  //   confirm({ title, message, confirmLabel, variant, destructive, ... })
  // `destructive: true` ou `danger: true` => variant 'error' (héritage appels existants).
  const confirm = useCallback((arg, options = {}) => {
    const cfg = typeof arg === 'string' ? { message: arg, ...options } : { ...arg };
    const variant = cfg.variant || ((cfg.destructive || cfg.danger) ? 'error' : 'warning');
    return new Promise((resolve) => {
      setState({
        title: cfg.title || null,
        message: cfg.message,
        resolve,
        variant,
        confirmLabel: cfg.confirmLabel || 'Confirmer',
        cancelLabel: cfg.cancelLabel || 'Annuler',
      });
    });
  }, []);

  const handleConfirm = () => { state?.resolve(true); setState(null); };
  const handleCancel = () => { state?.resolve(false); setState(null); };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        // Au-dessus de l'écran de blocage de la saisie quotidienne (100000) : dessous, la question
        // « Supprimer cette ligne ? » s'ouvrait invisible et la suppression ne partait jamais (20/10/2026).
        <div data-testid="confirm-dialog" style={{
          position: 'fixed', inset: 0, background: 'rgba(10,20,35,0.5)',
          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100010,
        }} onClick={handleCancel}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--surface, #fff)', borderRadius: 12, width: 420, maxWidth: 'calc(100vw - 32px)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
            border: '1px solid var(--border-light, #e8f0fe)',
          }}>
            <div style={{
              padding: '14px 20px', background: state.variant === 'error' ? '#c0392b' : 'var(--primary, #1a3a5c)',
              color: '#fff', fontWeight: 700, fontSize: 14,
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}>
              {state.title || (state.variant === 'error' ? 'Attention' : 'Confirmation')}
            </div>
            <div style={{
              padding: '20px', fontSize: 14, lineHeight: 1.6,
              color: 'var(--text, #1a1a2e)', fontFamily: "'IBM Plex Sans', sans-serif",
              whiteSpace: 'pre-line',
            }}>
              {state.message}
            </div>
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: 8,
              padding: '14px 20px', borderTop: '1px solid var(--border-light, #e8f0fe)',
              background: 'var(--bg, #f4f7fb)',
            }}>
              <button onClick={handleCancel} style={{
                padding: '8px 16px', border: '1px solid var(--border, #c0cfe0)', borderRadius: 6,
                background: 'var(--surface, #fff)', color: 'var(--text, #1a1a2e)',
                fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: "'IBM Plex Sans', sans-serif",
              }}>
                {state.cancelLabel}
              </button>
              <button onClick={handleConfirm} style={{
                padding: '8px 18px', border: 'none', borderRadius: 6,
                background: state.variant === 'error' ? '#c0392b' : 'var(--primary, #1a3a5c)',
                color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                fontFamily: "'IBM Plex Sans', sans-serif",
              }}>
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback to native confirm — supporte les deux signatures pour cohérence.
    return (arg) => {
      const msg = typeof arg === 'string' ? arg : (arg?.message || '');
      return Promise.resolve(window.confirm(msg));
    };
  }
  return ctx;
}

// ── Prompt (saisie d'une valeur) — remplace window.prompt() ──────────────────
// Même famille que ConfirmProvider : une modale du portail à la place du popup
// natif « support.meteris.fr indique que… ». Contrat aligné sur window.prompt :
//   const promptText = usePrompt();
//   const name = await promptText('Nom du dossier :', { defaultValue: '' });
//   → string (Entrée / Valider) ou null (Échap / Annuler / clic hors modale)
// Options : title, defaultValue, placeholder, confirmLabel, cancelLabel,
// multiline (textarea), required (Valider inactif tant que vide).
const PromptContext = createContext(null);

export function PromptProvider({ children }) {
  const [state, setState] = useState(null);
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  const promptText = useCallback((message, options = {}) => {
    const cfg = typeof message === 'string' ? { message, ...options } : { ...(message || {}) };
    return new Promise((resolve) => {
      setValue(cfg.defaultValue == null ? '' : String(cfg.defaultValue));
      setState({
        message: cfg.message || '',
        resolve,
        title: cfg.title || null,
        placeholder: cfg.placeholder || '',
        confirmLabel: cfg.confirmLabel || 'Valider',
        cancelLabel: cfg.cancelLabel || 'Annuler',
        multiline: Boolean(cfg.multiline),
        required: Boolean(cfg.required),
      });
    });
  }, []);

  useEffect(() => {
    if (state && inputRef.current) {
      inputRef.current.focus();
      if (typeof inputRef.current.select === 'function') inputRef.current.select();
    }
  }, [state]);

  const close = (result) => { state?.resolve(result); setState(null); };
  const canSubmit = !state?.required || value.trim().length > 0;
  const submit = () => { if (canSubmit) close(value); };
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(null); }
    else if (e.key === 'Enter' && !(state?.multiline && !e.ctrlKey)) { e.preventDefault(); submit(); }
  };

  const fieldStyle = {
    width: '100%', boxSizing: 'border-box', padding: '9px 12px', fontSize: 14,
    border: '1px solid var(--border, #c0cfe0)', borderRadius: 6,
    fontFamily: "'IBM Plex Sans', sans-serif", color: 'var(--text, #1a1a2e)',
    background: 'var(--surface, #fff)', outline: 'none',
  };

  return (
    <PromptContext.Provider value={promptText}>
      {children}
      {state && (
        <div role="dialog" aria-modal="true" data-testid="prompt-dialog" style={{
          position: 'fixed', inset: 0, background: 'rgba(10,20,35,0.5)',
          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100010,
        }} onClick={() => close(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--surface, #fff)', borderRadius: 12, width: 440, maxWidth: 'calc(100vw - 32px)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
            border: '1px solid var(--border-light, #e8f0fe)',
          }}>
            <div style={{
              padding: '14px 20px', background: 'var(--primary, #1a3a5c)',
              color: '#fff', fontWeight: 700, fontSize: 14,
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}>
              {state.title || 'Saisie'}
            </div>
            <div style={{
              padding: '20px', fontSize: 14, lineHeight: 1.6,
              color: 'var(--text, #1a1a2e)', fontFamily: "'IBM Plex Sans', sans-serif",
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              {state.message && <label style={{ whiteSpace: 'pre-line' }}>{state.message}</label>}
              {state.multiline ? (
                <textarea ref={inputRef} value={value} onChange={e => setValue(e.target.value)} onKeyDown={onKeyDown}
                  placeholder={state.placeholder} rows={4} style={{ ...fieldStyle, resize: 'vertical' }} />
              ) : (
                <input ref={inputRef} type="text" value={value} onChange={e => setValue(e.target.value)} onKeyDown={onKeyDown}
                  placeholder={state.placeholder} style={fieldStyle} />
              )}
              {state.multiline && (
                <span style={{ fontSize: 11, color: 'var(--text-muted, #6b7d8f)' }}>Ctrl+Entrée pour valider</span>
              )}
            </div>
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: 8,
              padding: '14px 20px', borderTop: '1px solid var(--border-light, #e8f0fe)',
              background: 'var(--bg, #f4f7fb)',
            }}>
              <button type="button" onClick={() => close(null)} style={{
                padding: '8px 16px', border: '1px solid var(--border, #c0cfe0)', borderRadius: 6,
                background: 'var(--surface, #fff)', color: 'var(--text, #1a1a2e)',
                fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: "'IBM Plex Sans', sans-serif",
              }}>
                {state.cancelLabel}
              </button>
              <button type="button" onClick={submit} disabled={!canSubmit} style={{
                padding: '8px 18px', border: 'none', borderRadius: 6,
                background: 'var(--primary, #1a3a5c)', color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: canSubmit ? 1 : 0.5,
                fontFamily: "'IBM Plex Sans', sans-serif",
              }}>
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </PromptContext.Provider>
  );
}

export function usePrompt() {
  const ctx = useContext(PromptContext);
  if (!ctx) {
    // Hors provider (tests, pages autonomes) : repli sur le natif, même contrat.
    return (message, options = {}) => {
      const msg = typeof message === 'string' ? message : (message?.message || '');
      const def = typeof message === 'string' ? options.defaultValue : message?.defaultValue;
      return Promise.resolve(window.prompt(msg, def == null ? '' : String(def)));
    };
  }
  return ctx;
}
